/**
 * components/UI/Sidebar.tsx - Navigation Sidebar Component
 * 
 * This component renders the left navigation sidebar with:
 * - Application branding
 * - Permission-aware navigation menu items
 * - Active state management
 * - User account section with login modal
 * - Role-based access control
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import { useTheme } from '../../contexts/ThemeContext';
import { formatRole } from '../../utils/auth';
import usePermissions from '../../hooks/usePermissions';
import styles from './Sidebar.module.css';

type Permission = 'dashboard' | 'vendors' | 'users' | 'events' | 'orders' | 'analytics';

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  permission?: Permission;
}

interface CurrentUser {
  id: string;
  name: string;
  role: string;
  phoneNumber: string;
  email: string;
  isAdmin: boolean;
  permissions: Permission[];
  vendorId?: string | null;
}

interface SidebarProps {
  activeMenu: string;
  selectedVendor?: { name: string; menuItems?: unknown[] };
  onMenuClick: (menuId: string) => void;
  promotionStats?: {
    activePromotions: number;
    totalClicks: number;
    averageCTR: number;
  };
  isOpen?: boolean;
  onClose?: () => void;
}

// Mock Icons - replace with your actual icons
const DashboardIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
  </svg>
);

const VendorsIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

const UsersIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-1c0-2.66 5.33-4 8-4s8 1.34 8 4v1H4zM12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z"/>
  </svg>
);

// NEW: Events & Promotions Icon
const EventsIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
  </svg>
);

const OrdersIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7 4V2c0-.55-.45-1-1-1s-1 .45-1 1v2c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2V2c0-.55-.45-1-1-1s-1 .45-1 1v2H7zm12 14H5V8h14v10z"/>
  </svg>
);

const AnalyticsIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
  </svg>
);

const UserIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
  </svg>
);

// Updated MENU_ITEMS with permission field
const MENU_ITEMS: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'dashboard', permission: 'dashboard' },
  { id: 'vendors', label: 'Vendors', icon: 'vendors', permission: 'vendors' },
  { id: 'users', label: 'Users', icon: 'users', permission: 'users' },
  { id: 'venue-settings', label: 'Venue Settings', icon: 'events' },
  { id: 'events', label: 'Events & Promotions', icon: 'events', permission: 'events' },
  { id: 'orders', label: 'Orders', icon: 'orders', permission: 'orders' },
  { id: 'analytics', label: 'Analytics', icon: 'analytics', permission: 'analytics' }
];

const Sidebar: React.FC<SidebarProps> = ({ activeMenu, selectedVendor, onMenuClick, promotionStats, isOpen = false, onClose }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { can, canManageVenueSettings } = usePermissions();
  const [user, setUser] = useState<CurrentUser | null>(null);

  const visibleMenuItems = MENU_ITEMS.filter((item) => {
    if (item.id === 'venue-settings') return canManageVenueSettings;
    if (!item.permission) return true;
    return can(item.permission);
  });

  // Load user from localStorage on component mount
  useEffect(() => {
    const storedUser = localStorage.getItem('oni_current_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Failed to parse user data:', error);
        localStorage.removeItem('oni_current_user');
      }
    }
  }, []);
  
  // Determine if a menu item should be highlighted
  const isMenuActive = (menuId: string): boolean => {
    return activeMenu === menuId && !selectedVendor;
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    // Redirect to login
    navigate('/login');
  };

  const handleAccountClick = () => {
    if (localStorage.getItem('oni_token')) {
      onMenuClick('profile');
      onClose?.();
    } else {
      navigate('/login');
    }
  };

  // Navigation item component with promotion badge
  const NavItem: React.FC<{ menuItem: MenuItem; isActive: boolean; onClick: (id: string) => void }> = ({ menuItem, isActive, onClick }) => {
    return (
      <button
        className={`${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
        onClick={() => { onClick(menuItem.id); onClose?.(); }}
        aria-current={isActive ? 'page' : undefined}
        aria-label={menuItem.label}
      >
        {/* Render appropriate icon */}
        {menuItem.icon === 'dashboard' && <DashboardIcon size={20} />}
        {menuItem.icon === 'vendors' && <VendorsIcon size={20} />}
        {menuItem.icon === 'users' && <UsersIcon size={20} />}
        {menuItem.icon === 'events' && <EventsIcon size={20} />}
        {menuItem.icon === 'orders' && <OrdersIcon size={20} />}
        {menuItem.icon === 'analytics' && <AnalyticsIcon size={20} />}

        <span className={styles.navItemLabel}>
          {menuItem.label}
        </span>

        {/* Show badge for Events & Promotions if there are active campaigns */}
        {menuItem.id === 'events' && promotionStats && promotionStats.activePromotions > 0 && (
          <span className={styles.navBadge} aria-label={`${promotionStats.activePromotions} active campaigns`}>
            {promotionStats.activePromotions}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div className={styles.overlay} onClick={onClose} aria-hidden="true" />
      )}

      <div className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''}`}>

        {/* Header/Branding Section */}
        <div className={styles.header}>
          <div className={styles.headerRow}>
            <div>
              <h2 className={styles.title}>
                Admin Panel
              </h2>
              <p className={styles.subtitle}>
                Stadium Management
              </p>
            </div>
            {/* Mobile close button */}
            <button className={styles.closeButton} onClick={onClose} aria-label="Close menu">
              <svg width={20} height={20} viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className={styles.nav}>
          <div className={styles.menuSection}>
            <p className={styles.menuSectionLabel}>
              Main Menu
            </p>
            
            {visibleMenuItems.map(menuItem => (
              <NavItem
                key={menuItem.id}
                menuItem={menuItem}
                isActive={isMenuActive(menuItem.id)}
                onClick={onMenuClick}
              />
            ))}
          </div>

          {/* Promotions Summary - Show when events tab is active */}
          {activeMenu === 'events' && promotionStats && (
            <div className={styles.contextIndicator}>
              <p className={styles.contextLabel}>
                Promotions Overview
              </p>
              <div className={styles.promotionSummary}>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryText}>Active Campaigns</span>
                  <span className={styles.summaryValue}>{promotionStats.activePromotions}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryText}>Total Clicks</span>
                  <span className={styles.summaryValue}>{promotionStats.totalClicks.toLocaleString()}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryText}>Avg. CTR</span>
                  <span className={styles.summaryValue}>{promotionStats.averageCTR.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          )}

          {/* Current Vendor Context - Show when managing a vendor */}
          {selectedVendor && (
            <div className={styles.contextIndicator}>
              <p className={styles.contextLabel}>
                Currently Managing
              </p>
              <p className={styles.contextName}>
                {selectedVendor.name}
              </p>
              <p className={styles.contextMeta}>
                Menu Items: {selectedVendor.menuItems?.length || 0}
              </p>
            </div>
          )}
        </nav>

        {/* Account Section */}
        <div className={styles.accountSection}>
          <div 
            className={styles.accountButton}
            onClick={handleAccountClick}
          >
            {/* User Avatar/Icon */}
            <div className={`${styles.avatar} ${user ? styles.avatarLoggedIn : styles.avatarLoggedOut}`}>
              {user ? (
                user.name.charAt(0).toUpperCase()
              ) : (
                <UserIcon size={20} />
              )}
            </div>

            {/* User Info */}
            <div className={styles.accountInfo}>
              {user ? (
                <>
                  <p className={styles.userName}>
                    {user.name}
                  </p>
                  <p className={styles.userRole}>
                    {formatRole(user.role)}
                  </p>
                  <p className={styles.userMeta}>
                    View profile
                  </p>
                </>
              ) : (
                <>
                  <p className={styles.signInPrompt}>
                    Sign In
                  </p>
                  <p className={styles.signInMeta}>
                    Click to login
                  </p>
                </>
              )}
            </div>

            {/* Status Indicator */}
            {user && (
              <div
                className={`${styles.statusIndicator} ${styles.statusOnline}`}
                role="img"
                aria-label="Online"
                title="Online"
              />
            )}
          </div>
          {user && (
            <button className={styles.logoutButton} onClick={handleLogout}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
              </svg>
              Logout
            </button>
          )}
        </div>

        {/* Footer/Version Info */}
        <div className={styles.footer}>
          <button
            onClick={toggleTheme}
            className={styles.themeToggle}
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            aria-pressed={theme === 'dark'}
          >
            {theme === 'dark' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-12.37l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06c.39-.39.39-1.03 0-1.41s-1.03-.39-1.41 0zM7.05 18.36l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06c.39-.39.39-1.03 0-1.41s-1.03-.39-1.41 0z"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z"/>
              </svg>
            )}
            <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
          </button>
          <p className={styles.version}>ONI Admin v1.0.0</p>
        </div>
      </div>
    </>
  );
};

export default Sidebar;